import { Editor } from "@tinymce/tinymce-react"
import { useEffect, useRef, useState } from "react"
import Modal from "react-modal"
import { DrawIoEmbed, DrawIoEmbedRef } from "react-drawio"
import Button from "../button/button"
import { uploadImage } from "../../api/image"
import { buildDiagramExportAction } from "../tiptapEditor/utils/diagramExport"
import {
  DIAGRAM_DATA_ATTR,
  DIAGRAM_DISPLAY_STYLE,
  DRAWIO_EMBED_URL_PARAMS,
  diagramAltLabel,
} from "../tiptapEditor/utils/diagramConstants"
import { encodeDiagramXml } from "../tiptapEditor/utils/diagramXml"
import "./textEditor.scss"

type ITextEditorProps = {
  id?: string
  value?: string
  height?: string | number
  handleOnChange?: (content: string) => void
  label?: string
}

const customStyles = {
  content: {
    display: "flex",
    flexDirection: "column",
    gap: 24,
  },
}

const TextEditor = ({
  id,
  value = "",
  height = 300,
  handleOnChange,
  label = "",
}: ITextEditorProps) => {
  const drawioRef = useRef<DrawIoEmbedRef>(null)
  const editorRef = useRef<any>(null)
  const [isDrawioOpen, setIsDrawioOpen] = useState(false)
  const [existingImages, setExistingImages] = useState<string[]>([])

  const exportData = () => {
    drawioRef.current?.exportDiagram(buildDiagramExportAction())
  }

  const handleEditorChange = (content: string) => {
    handleOnChange && handleOnChange(content)
  }

  const handleSaveDrawio = (data: { data: string; xml?: string }) => {
    if (!data?.data || !handleOnChange) return

    const encodedXml = data.xml ? encodeDiagramXml(data.xml) : ""
    const imgTag = `<img src="${data.data}" alt="${diagramAltLabel("geometry")}" style="${DIAGRAM_DISPLAY_STYLE}" ${DIAGRAM_DATA_ATTR.contentType}="geometry"${encodedXml ? ` ${DIAGRAM_DATA_ATTR.diagramXml}="${encodedXml}"` : ""} />`

    if (editorRef.current) {
      editorRef.current.insertContent(imgTag)
    } else {
      handleOnChange(value + imgTag)
    }

    setIsDrawioOpen(false)
  }

  const handleImageUpload = (
    blobInfo: any,
    progress: (percent: number) => void
  ): Promise<string> => {
    return new Promise(async (resolve, reject) => {
      try {
        const file = blobInfo.blob()
        const fileURL = URL.createObjectURL(file)

        console.log({ fileURL, file, value }, "Uploading image")

        if (existingImages.includes(fileURL)) {
          resolve(fileURL)
          return
        }

        const formData = new FormData()
        formData.append("file", file, blobInfo.filename())

        const response = await uploadImage(formData)

        if (
          !response.data ||
          typeof response.data.data.final_signed_url !== "string"
        ) {
          reject(new Error("Invalid response from server"))
          return
        }

        setExistingImages((prev) => [
          ...prev,
          response.data.data.final_signed_url,
        ])

        resolve(response.data.data.final_signed_url)
      } catch (error) {
        reject(new Error("Image upload failed"))
      }
    })
  }

  useEffect(() => {
    if (window.MathJax) {
      window.MathJax.typesetPromise().then(() =>
        console.log("MathJax Rendered Globally")
      )
    }
  }, [])

  return (
    <div className='text_editor'>
      {label && (
        <div style={{ marginBottom: 2 }}>
          <label>{label}</label>
        </div>
      )}
      <Editor
        id={id}
        apiKey={process.env.REACT_APP_TINYMCE}
        value={value}
        onInit={(evt, editor) => (editorRef.current = editor)}
        init={{
          images_upload_handler: handleImageUpload,
          automatic_uploads: false,
          placeholder: "",
          height: height,
          width: "100%",
          draggable_modal: true,
          extended_valid_elements: "*[.*]",
          plugins: [
            "advlist",
            "autolink",
            "lists",
            "link",
            "image",
            "charmap",
            "preview",
            "anchor",
            "searchreplace",
            "visualblocks",
            "code",
            "fullscreen",
            "insertdatetime",
            "media",
            "table",
            "help",
            "wordcount",
            "tiny_mce_wiris",
            "drawio",
            "lineheight",
            "emoticons",
            "codesample",
            "save",
            "print",
            "pagebreak",
            "addtemplate",
            "inserttemplate",
            "addcomment",
            "showcomments",
            "ltr",
            "rtl",
            "visualchars",
            "nonbreaking",
            "tiny_mce_wiris",
          ],
          toolbar: `
    undo redo | revisionhistory  | suggestededits  | formatselect fontsizeinput  bold italic underline strikethrough |
    forecolor backcolor | alignleft aligncenter alignright alignjustify |
    bullist numlist outdent indent | blockquote subscript superscript | removeformat |
     emoticons | link image media math table drawio  |
    tiny_mce_wiris_formulaEditor | tiny_mce_wiris_formulaEditorChemistry |
     lineheight pagebreak anchor codesample |
    addtemplate inserttemplate | addcomment showcomments | ltr rtl casechange |
    code fullscreen preview save print | help
  `,

          fontsize_formats: "8pt 10pt 12pt 14pt 18pt 24pt 36pt",
          menubar: "file edit view insert format tools table help",

          setup: (editor: any) => {
            editor.on("init", () => {
              setTimeout(() => {
                if (window.MathJax) {
                  window.MathJax.typesetPromise([editor.getBody()])
                    .then(() => console.log("MathJax Rendered in TinyMCE"))
                    .catch((err: any) =>
                      console.error(" MathJax Init Error:", err)
                    )
                } else {
                  console.error(" MathJax is not loaded")
                }
              }, 500)
            })

            editor.on("change", () => {
              if (window.MathJax) {
                setTimeout(() => {
                  window.MathJax.typesetPromise([editor.getBody()])
                    .then(() =>
                      console.log(" MathJax Re-rendered on Content Change")
                    )
                    .catch((err: any) =>
                      console.error("MathJax Change Error:", err)
                    )
                }, 500)
              }
            })

            editor.ui.registry.addButton("drawio", {
              text: "Draw",
              onAction: () => setIsDrawioOpen(true),
            })
          },
          external_plugins: {
            tiny_mce_wiris: `https://examcreator.dev.olive.media/tinymce_plugins/mathtype/mathtype-tinymce6/plugin.min.js`,
          },
          forced_root_block: "div",
        }}
        onEditorChange={(value: any) => {
          handleEditorChange(value)
        }}
      />

      <Modal
        isOpen={isDrawioOpen}
        style={customStyles}
        onRequestClose={() => setIsDrawioOpen(false)}
      >
        <div>
          <Button
            type='secondary'
            text='Export'
            onClick={exportData}
            style={{
              height: 40,
              marginLeft: "auto",
              marginBottom: 16,
            }}
          />
          <DrawIoEmbed
            ref={drawioRef}
            onExport={handleSaveDrawio}
            urlParameters={DRAWIO_EMBED_URL_PARAMS}
          />
        </div>
      </Modal>
    </div>
  )
}

export default TextEditor
